import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { CATEGORY_KEYS } from "@/i18n";
import { resolveImg } from "@/contexts/SettingsContext";
import ImageUploadField from "@/components/admin/ImageUploadField";

const field = "vl-input rounded-lg px-4 py-2.5 w-full";
const CATS = [...CATEGORY_KEYS, "accessoires"];
const empty = { category: "brautmode", title: "", subcategory: "", description: "", image: "", order: 0 };

export default function ProductsPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/products").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title || !editing.image) { toast.error(t("required_field")); return; }
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, editing);
      else await api.post("/admin/products", editing);
      toast.success(t("save")); setEditing(null); load();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/products/${id}`); setItems((x) => x.filter((p) => p.id !== id)); } catch (e) { toast.error(errMsg(e.response?.data?.detail)); } };

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin text-[var(--vl-gold)] mx-auto" /></div>;

  return (
    <div data-testid="products-panel">
      <button onClick={() => setEditing({ ...empty })} className="vl-btn-gold rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 mb-5" data-testid="add-product-btn"><Plus size={15} /> {t("add_item")}</button>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="vl-card rounded-xl overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="h-40 overflow-hidden">{p.image && <img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="text-[10px] tracking-widest uppercase text-[var(--vl-gold)]">{t(`cat_${p.category}`) || p.category}</div>
              <h4 className="text-[var(--vl-ivory)] font-medium mt-1">{p.title}</h4>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(p)} className="vl-btn-outline rounded-full px-4 py-1.5 text-xs">{t("edit_label")}</button>
                <button onClick={() => del(p.id)} className="text-red-300 px-2"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)} />
          <div className="relative vl-card rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" data-testid="product-editor">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif-display text-xl text-[var(--vl-ivory)]">{t("products_tab")}</h3>
              <button onClick={() => setEditing(null)}><X size={20} className="text-[var(--vl-stone)]" /></button>
            </div>
            <div className="space-y-3">
              <select className={field} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="product-category">
                {CATS.map((c) => <option key={c} value={c}>{t(`cat_${c}`) || c}</option>)}
              </select>
              <input className={field} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder={t("title_label")} data-testid="product-title" />
              <input className={field} value={editing.subcategory} onChange={(e) => setEditing({ ...editing, subcategory: e.target.value })} placeholder={t("subcategory_label")} />
              <textarea className={field} rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder={t("description_label")} />
              <div><label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("image_label")}</label><ImageUploadField value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} testid="product-image-upload" /></div>
              <input type="number" className={field} value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })} placeholder={t("order_label")} />
            </div>
            <button onClick={save} className="vl-btn-gold rounded-full px-6 py-2.5 text-sm font-medium mt-5 w-full" data-testid="save-product-btn">{t("save")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
